import { TransformedXAxisType } from '@api';
import { formatDecimalPlaces } from '@design/components/table/utils';
import { useWindowResize } from '@helpers';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { RemoteLegend } from '../RemoteLegend';
import { tooltipFormatter } from '../utils';

const defaultSeriesNames = ['Increased', 'Decreased'];
const MINUS_SIGN = '-';

type WaterfallSeries = {
  name: string;
  data: (number | null)[];
};

export interface WaterfallChartProps extends ApacheChartBase {
  /** Discriminator. Always `'apache-waterfall'`. */
  chartType: 'apache-waterfall';
  /** Categories rendered on the x-axis (one per bar). */
  xAxis: TransformedXAxisType;
  /** Signed values per category. Positive values build the cumulative bar up; negative values build it down. */
  series: number[];
  /** Names used for the legend / tooltip. Defaults to `['Increased', 'Decreased']`. */
  seriesNames?: string[];
}

/**
 * Waterfall chart: stacked bars showing how positive and negative
 * deltas accumulate from a starting value.
 *
 * Splits each bar into a transparent placeholder plus a coloured
 * delta, which together produce the classic floating-bar visual.
 * Positive deltas use the `seriesNames[0]` series colour; negative
 * deltas use `seriesNames[1]`.
 */
export const WaterfallChart = ({
  theme = 'dark_default',
  chartType,
  xAxis,
  series,
  seriesNames = defaultSeriesNames,
  onInit,
  isModal,
}: WaterfallChartProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const [legendInstance, setLegendInstance] = useState<Types.ECharts | null>(
    null
  );
  const { width, height } = useWindowResize({ targetRef: chartRef });

  useEffect(() => {
    if (chartRef.current) {
      const chartDom = getInstanceByDom(chartRef.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  const formattedSeries = useMemo(() => {
    return series.reduce<WaterfallSeries[]>(
      (result, current) => {
        if (current >= 0) {
          // Positive values go to "Increased"(first) series
          result[0].data.push(current);
          result[1].data.push(null);
        } else {
          // Negative values go to "Decreased"(second) series
          result[0].data.push(null);
          result[1].data.push(Math.abs(current));
        }

        return result;
      },
      [
        { name: seriesNames[0], data: [] },
        { name: seriesNames[1], data: [] },
      ]
    );
  }, [series, seriesNames]);

  const placeholderData = useMemo(() => {
    if (!series.length) {
      return [];
    }

    const result: number[] = [];
    let cumulativeSum = 0;

    for (let i = 0; i < series.length; i++) {
      if (series[i] >= 0) {
        // For positive values: placeholder is at the start (cumulativeSum)
        result.push(cumulativeSum);
      } else {
        // For negative values: placeholder is at the end (cumulativeSum + value)
        result.push(cumulativeSum + series[i]);
      }

      cumulativeSum += series[i];
    }

    return result;
  }, [series]);

  useEffect(() => {
    const chartDom = init(chartRef.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    setLegendInstance(chartDom);

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        animationDuration: 2_000,
        xAxis: {
          type: 'category',
          data: xAxis,
          axisLabel: {
            interval: 0,
            rotate: 45,
            margin: 10,
            align: 'right',
          },
        },
        yAxis: {
          type: 'value',
        },
        grid: {
          bottom: '5%',
        },
        legend: {
          show: false,
        },
        series: [
          {
            name: 'placeholder',
            type: 'bar',
            stack: 'Total',
            silent: true,
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent',
            },
            emphasis: {
              itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
              },
            },
            data: placeholderData,
          },
          {
            ...formattedSeries[0],
            type: 'bar',
            stack: 'Total',
            label: {
              show: true,
              position: 'top',
              formatter: (
                params: echarts.DefaultLabelFormatterCallbackParams
              ) => {
                const value = params.value || 0;
                return formatDecimalPlaces(Number(value)).toString();
              },
            },
          },
          {
            ...formattedSeries[1],
            type: 'bar',
            stack: 'Total',
            label: {
              show: true,
              position: 'bottom',
              formatter: (
                params: echarts.DefaultLabelFormatterCallbackParams
              ) => {
                const value = params.value || 0;
                return `${MINUS_SIGN}${formatDecimalPlaces(
                  Number(value)
                ).toString()}`;
              },
            },
          },
        ],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: params => {
            if (Array.isArray(params)) {
              const increasedValue = params[1]?.value;

              const validIncreasedValue =
                increasedValue !== 0 &&
                increasedValue !== undefined &&
                increasedValue !== null;

              return tooltipFormatter({
                isShowTitle: true,
                isShowMarker: true,
                params: validIncreasedValue ? params[1] : params[2],
              });
            }

            return tooltipFormatter({
              isShowTitle: true,
              isShowMarker: true,
              params,
            });
          },
        },
      },
    });

    chartDom.setOption(option);

    if (wrapperRef.current) {
      onInit?.(wrapperRef.current);
    }

    return () => {
      chartDom.dispose();
      setLegendInstance(null);
    };
  }, [chartType, theme, xAxis, series, seriesNames, width, height, isModal]);

  return (
    <div ref={wrapperRef}>
      <div ref={chartRef} />
      {legendInstance && (
        <RemoteLegend
          theme={theme}
          legendData={seriesNames.map(name => ({ name, type: 'bar' }))}
          targetChart={legendInstance}
        />
      )}
    </div>
  );
};
